import {objectFromEntries} from '@benchling/js-utils';
import isEqual from 'react-fast-compare';

import {MinimalSchemaTag} from 'folder-items/FileConstants';
import {PlateMap, PlateMapWell} from 'samples/plate-maps/PlateMapConstants';
import {wellPositionIterator} from 'samples/plate-maps/PlateMapUtil';
import {
  getDefaultMeasurement,
  getDefaultMeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {PlateSchema, PlateSize} from 'samples/storables/StorableSchemaConstants';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getInitialTags} from 'schemas/tags/TagUtil';

// Serialized form of MinimalSchemaTag stored in persisted data frames
export interface SerializedSchemaTag {
  fieldType: FieldType;
  name: string;
  value: unknown;
}

export function serializeSchemaTag(tag: MinimalSchemaTag): SerializedSchemaTag {
  const ft = tag.tagSchemaField.fieldType;
  let value: unknown;
  if (ft === FieldType.FLOAT) {
    value = tag.floatValue ?? null;
  } else if (ft === FieldType.INTEGER) {
    value = tag.integerValue ?? null;
  } else if (ft === FieldType.BOOLEAN) {
    value = tag.booleanValue ?? null;
  } else if (ft === FieldType.DATE) {
    value = tag.dateValue ?? null;
  } else if (ft === FieldType.DATETIME) {
    value = tag.datetimeValue ?? null;
  } else if (ft === FieldType.SELECTOR) {
    value = tag.schemaFieldSelectorOptionIds ?? null;
  } else {
    value = tag.value ?? null; // TEXT, LONG_TEXT, link types
  }
  return {fieldType: ft, name: tag.tagSchemaField.name, value};
}

export function deserializeSchemaTag(serialized: SerializedSchemaTag): MinimalSchemaTag {
  const base: MinimalSchemaTag = {
    tagSchemaField: {fieldType: serialized.fieldType, name: serialized.name},
  };
  const {fieldType, value} = serialized;
  if (fieldType === FieldType.FLOAT) {
    return {...base, floatValue: value as number | null};
  } else if (fieldType === FieldType.INTEGER) {
    return {...base, integerValue: value as number | null};
  } else if (fieldType === FieldType.BOOLEAN) {
    return {...base, booleanValue: value as boolean | null};
  } else if (fieldType === FieldType.DATE) {
    return {...base, dateValue: value as string | null};
  } else if (fieldType === FieldType.DATETIME) {
    return {...base, datetimeValue: value as string | null};
  } else if (fieldType === FieldType.SELECTOR) {
    return {...base, schemaFieldSelectorOptionIds: value as Array<string> | null};
  } else {
    return {...base, value: value as string | null};
  }
}

export function deserializeSchemaTags(
  raw: Record<string, SerializedSchemaTag> | null
): PlateMapWell['schemaTags'] {
  if (raw == null) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(raw).map(([id, serialized]) => [id, deserializeSchemaTag(serialized)])
  );
}

export enum ScratchPlateDesignModalStep {
  DEFINE_SETTINGS = 'DEFINE_SETTINGS',
  DEFINE_PLATE_MAP = 'DEFINE_PLATE_MAP',
}

export function getScratchPlateDesignModalSteps(): Array<{
  key: ScratchPlateDesignModalStep;
  title: string;
}> {
  return [
    {key: ScratchPlateDesignModalStep.DEFINE_SETTINGS, title: 'Define plate settings'},
    {key: ScratchPlateDesignModalStep.DEFINE_PLATE_MAP, title: 'Define plate map'},
  ];
}

export function getNextScratchPlateDesignModalStep(
  currentStepKey: ScratchPlateDesignModalStep
): ScratchPlateDesignModalStep {
  const orderedStepKeys = getScratchPlateDesignModalSteps().map((step) => step.key);
  const currentIdx = orderedStepKeys.findIndex((stepKey) => stepKey === currentStepKey);
  if (currentIdx === -1) {
    throw new Error(`Unexpected step key: ${currentStepKey}`);
  }

  const nextStep = orderedStepKeys.at(currentIdx + 1);
  if (nextStep == null) {
    throw new Error('Function called unexpectedly on last step');
  }
  return nextStep;
}

export function getPreviousScratchPlateDesignModalStep(
  currentStepKey: ScratchPlateDesignModalStep
): ScratchPlateDesignModalStep {
  const orderedStepKeys = getScratchPlateDesignModalSteps().map((step) => step.key);
  const currentIdx = orderedStepKeys.findIndex((stepKey) => stepKey === currentStepKey);
  if (currentIdx === -1) {
    throw new Error(`Unexpected step key: ${currentStepKey}`);
  } else if (currentIdx === 0) {
    throw new Error('Function called unexpectedly on first step');
  }

  return orderedStepKeys.at(currentIdx - 1)!;
}

export function isLastScratchPlateDesignModalStep(currentStepKey: ScratchPlateDesignModalStep): boolean {
  const orderedStepKeys = getScratchPlateDesignModalSteps().map((step) => step.key);
  return orderedStepKeys.at(-1) === currentStepKey;
}

export interface ScratchPlateDesignState {
  name: string | null;
  plateSize: PlateSize | null;
  plateSchema: PlateSchema | null;
  plateMap: PlateMap | null;
}

export const INITIAL_SCRATCH_PLATE_DESIGN_MODAL_STATE: ScratchPlateDesignState = {
  name: null,
  plateSize: null,
  plateSchema: null,
  plateMap: null,
};

/**
 * Resizes a PlateMap with new dimensions, preserving well data for positions that exist in both
 * the old and new dimensions. Wells outside the new bounds are dropped; new positions are empty. Schema tags
 * are dropped.
 *
 * No-ops if the plate is not actually being resized.
 */
export function resizePlateMap(plateMap: PlateMap, newHeight: number, newWidth: number): PlateMap {
  const newSize = {numRows: newHeight, numColumns: newWidth};

  if (isEqual(plateMap.size, newSize)) {
    return plateMap;
  }

  const oldWellsByPosition = new Map<string, PlateMapWell>(
    plateMap.wells.map((well) => [`${well.position.row},${well.position.column}`, well])
  );
  const wells: Array<PlateMapWell> = [];
  for (const {row, column} of wellPositionIterator(newSize)) {
    const existing = oldWellsByPosition.get(`${row},${column}`);
    wells.push(
      existing != null
        ? {...existing, schemaTags: {}}
        : {
            position: {row, column},
            role: null,
            sources: [],
            contents: [],
            schemaTags: {},
            dataParameterTags: {},
            quantity: getDefaultMeasurement(),
            finalQuantity: getDefaultMeasurementWithErrors(),
            concentrationLayers: {},
          }
    );
  }
  return {...plateMap, size: newSize, wells};
}

/**
 * Creates an empty PlateMap from height/width, with no schemaTags defined.
 */
export function getEmptyPlateMapForScratchPlate(height: number, width: number): PlateMap {
  const size = {numRows: height, numColumns: width};
  const wells: Array<PlateMapWell> = [];
  for (const {row, column} of wellPositionIterator(size)) {
    wells.push({
      position: {row, column},
      role: null,
      sources: [],
      contents: [],
      schemaTags: {},
      dataParameterTags: {},
      quantity: getDefaultMeasurement(),
      finalQuantity: getDefaultMeasurementWithErrors(),
      concentrationLayers: {},
    });
  }
  return {
    id: null,
    size,
    wells,
    dataParameterNamesByVersionId: {},
    testOrderDilutionInfoById: {},
  };
}

export function addInitialSchemaTagsToPlateMap(
  plateMap: PlateMap,
  schemaFields: Array<SchemaField>
): PlateMap {
  const initialWellSchemaTags = objectFromEntries(
    getInitialTags(schemaFields).map((tag) => [tag.tagSchemaField.apiId, tag])
  );
  return {
    ...plateMap,
    wells: plateMap.wells.map((well) => ({...well, schemaTags: {...initialWellSchemaTags}})),
  };
}

export function removeSchemaTagsFromPlateMap(plateMap: PlateMap): PlateMap {
  return {
    ...plateMap,
    wells: plateMap.wells.map((well) => ({...well, schemaTags: {}})),
  };
}
